import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  IoSendOutline,
  IoBookmarkOutline,
  IoAttachOutline,
  IoImageOutline,
  IoMicOutline,
  IoSearchOutline,
  IoClose,
  IoArrowUndoOutline,
  IoCheckmark,
  IoCheckmarkDone,
  IoCopyOutline,
  IoShareOutline,
  IoTrashOutline,
  IoStorefrontOutline,
  IoMegaphoneOutline,
  IoCheckboxOutline,
  IoTrashBinOutline,
} from "react-icons/io5";
import api, { fileUrl, API } from "@/api";
import { useI18n, STORE_LABELS } from "@/i18n";
import { useAuth } from "@/auth";
import { AppHeader } from "@/components/AppHeader";
import { StoreAvatar } from "@/components/StoreAvatar";
import { AudioRecorder } from "@/components/AudioRecorder";
import { useStoreImages } from "@/hooks/useStoreImages";
import { formatDateTimeIT } from "@/utils";
import { compressImage } from "@/utils/image";
import { tapLight, tapSuccess, tapError } from "@/utils/haptics";

const BROADCAST = "broadcast";
const SWIPE_THRESHOLD = 60;

function resolveAttachmentUrl(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  if (url.startsWith("/api/files/")) {
    const id = url.replace("/api/files/", "");
    return fileUrl(id);
  }
  return url;
}

function highlight(text, query) {
  if (!query) return text;
  const parts = text.split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi"));
  return parts.map((p, i) =>
    p.toLowerCase() === query.toLowerCase() ? <mark key={`hl-${i}-${p}`} style={{ background: "var(--tf-yellow-soft, #FEF3C7)", padding: "0 2px", borderRadius: 3 }}>{p}</mark> : p,
  );
}

export default function ChatDetailPage() {
  const { convId } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user, isTitolare } = useAuth();
  const storeImages = useStoreImages();
  const isBroadcast = convId === BROADCAST;
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [typing, setTyping] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [audioOpen, setAudioOpen] = useState(false);
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  const [actionsheetMsg, setActionsheetMsg] = useState(null);
  const [forwardOpen, setForwardOpen] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const bottomRef = useRef(null);
  const fileImgRef = useRef(null);
  const typingTimerRef = useRef(null);
  const myReactor = user?.storeId || (isTitolare ? "titolare" : "");

  const parts = (convId || "").split("__");
  const other = parts.find((p) => p !== user?.storeId) || parts[0];
  const title = isBroadcast
    ? "Tutti i negozi"
    : isTitolare
      ? `${t(STORE_LABELS[parts[0]])} · ${t(STORE_LABELS[parts[1]])}`
      : t(STORE_LABELS[other]);

  // Can the current user post in this conversation?
  // - Broadcast: everyone (including titolare)
  // - 1-to-1: only direttori of the two involved stores
  const canPost = isBroadcast || !isTitolare;

  // Merge server list with any pending optimistic (temp-*) entries we sent locally.
  const mergeWithPending = useCallback((serverList) => {
    setMessages((prev) => {
      const pending = prev.filter((m) => m.id && String(m.id).startsWith("temp-"));
      // If a pending temp msg's text matches a very recent server msg from the same sender, drop it.
      const alive = pending.filter((p) => {
        return !serverList.some(
          (s) =>
            s.sender_store === p.sender_store &&
            (s.text || "") === (p.text || "") &&
            !String(s.id).startsWith("temp-") &&
            Math.abs(new Date(s.created_at) - new Date(p.created_at)) < 20000,
        );
      });
      return [...serverList, ...alive];
    });
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/messages", { params: { conv_id: convId } });
      mergeWithPending(data);
      api.post(`/chat/conversations/${convId}/read`).catch(() => null);
      window.dispatchEvent(new CustomEvent("chat-unread-changed"));
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 60);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
      navigate(-1);
    } finally { setLoading(false); }
  }, [convId, navigate, mergeWithPending]);

  useEffect(() => { load(); }, [load]);

  // Poll for new messages + typing status every 3s (paused when tab hidden)
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      if (!alive || document.hidden) return;
      try {
        const [msgs, tp] = await Promise.all([
          api.get("/chat/messages", { params: { conv_id: convId } }),
          api.get(`/chat/typing/${convId}`),
        ]);
        if (!alive) return;
        mergeWithPending(msgs.data);
        setTyping(tp.data?.typing || []);
      } catch { /* offline */ }
    };
    const iv = setInterval(tick, 3000);
    const onVis = () => { if (!document.hidden) tick(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { alive = false; clearInterval(iv); document.removeEventListener("visibilitychange", onVis); };
  }, [convId]);

  const sendTypingBeat = () => {
    if (typingTimerRef.current) return;
    api.post("/chat/typing", { conv_id: convId }).catch(() => null);
    typingTimerRef.current = setTimeout(() => { typingTimerRef.current = null; }, 3000);
  };

  const reactToMessage = async (msg, emoji) => {
    try {
      // Optimistic
      setMessages((prev) => prev.map((m) => {
        if (m.id !== msg.id) return m;
        const cur = { ...(m.reactions || {}) };
        const list = [...(cur[emoji] || [])];
        const idx = list.indexOf(myReactor);
        if (idx >= 0) list.splice(idx, 1);
        else list.push(myReactor);
        if (list.length) cur[emoji] = list; else delete cur[emoji];
        return { ...m, reactions: cur };
      }));
      tapLight();
      await api.post(`/chat/messages/${msg.id}/react`, { emoji, action: "toggle" });
    } catch (e) {
      tapError();
      toast.error(e?.response?.data?.detail || "Errore reazione");
    }
  };

  const copyMessage = async (msg) => {
    const txt = msg.text || (msg.attachment_type === "image" ? "📷 Foto" : msg.attachment_type === "audio" ? "🎤 Audio" : "");
    if (!txt) { toast.info("Nessun testo da copiare"); return; }
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(txt);
      } else {
        // Fallback
        const ta = document.createElement("textarea");
        ta.value = txt; document.body.appendChild(ta); ta.select();
        document.execCommand("copy"); ta.remove();
      }
      tapSuccess();
      toast.success("Copiato negli appunti");
    } catch {
      toast.error("Copia non riuscita");
    }
    setActionsheetMsg(null);
  };

  const deleteMessage = async (msg) => {
    if (!msg) return;
    // Optimistic
    const snapshot = messages;
    setMessages((prev) => prev.filter((m) => m.id !== msg.id));
    setActionsheetMsg(null);
    try {
      await api.delete(`/chat/messages/${msg.id}`);
      tapSuccess();
      toast.success("Messaggio eliminato");
    } catch (e) {
      // Rollback
      setMessages(snapshot);
      tapError();
      toast.error(e?.response?.data?.detail || "Errore eliminazione");
    }
  };

  /* ---------- Multi-select ---------- */
  const enterSelectMode = (initialId) => {
    setSelectMode(true);
    setActionsheetMsg(null);
    setSelectedIds(new Set(initialId ? [initialId] : []));
  };
  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
  };
  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAll = () => {
    setSelectedIds(new Set(messages.map((m) => m.id)));
  };
  const deleteSelected = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    // Only titolare (or owner of each msg) can delete; backend enforces.
    const snapshot = messages;
    setMessages((prev) => prev.filter((m) => !selectedIds.has(m.id)));
    exitSelectMode();
    let ok = 0, fail = 0;
    await Promise.all(ids.map(async (id) => {
      try { await api.delete(`/chat/messages/${id}`); ok += 1; }
      catch { fail += 1; }
    }));
    if (fail === 0) { tapSuccess(); toast.success(`${ok} messaggi eliminati`); }
    else if (ok === 0) { tapError(); setMessages(snapshot); toast.error("Nessun messaggio eliminato"); }
    else { toast.warning(`${ok} eliminati, ${fail} non eliminati`); }
  };

  const forwardTo = async (destination) => {
    if (!actionsheetMsg) return;
    const msgId = actionsheetMsg.msg?.id;
    if (!msgId) return;
    try {
      const payload = destination === "broadcast"
        ? { to_broadcast: true }
        : { to_store: destination };
      const { data } = await api.post(`/chat/messages/${msgId}/forward`, payload);
      tapSuccess();
      toast.success("Messaggio inoltrato");
      setForwardOpen(false);
      setActionsheetMsg(null);
      if (data?.conv_id) navigate(`/chat/${data.conv_id}`);
    } catch (e) {
      tapError();
      toast.error(e?.response?.data?.detail || "Inoltro fallito");
    }
  };

  const doSend = async (payload) => {
    setSending(true);
    // Optimistic UI: show the message immediately with a tempId, replace after server ack.
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const optimistic = {
      id: tempId,
      text: payload.text || "",
      sender_store: user?.storeId || "",
      sender_user: user?.name || "",
      attachment_type: payload.attachment_type || null,
      attachment_url: null,
      created_at: new Date().toISOString(),
      read_by_other: false,
      __pending: true,
    };
    if (!payload.attachment_file_id) {
      // Only optimistically show text messages (attachments need a real URL from server)
      setMessages((prev) => [...prev, optimistic]);
      setText("");
      setReplyTo(null);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 30);
    }
    try {
      const body = {
        text: payload.text || "",
        reply_to_id: replyTo?.id || null,
        attachment_file_id: payload.attachment_file_id || null,
        attachment_type: payload.attachment_type || null,
        attachment_duration: payload.attachment_duration || null,
      };
      if (isBroadcast) body.conv_id = BROADCAST;
      else body.other_store = other;
      await api.post("/chat/messages", body);
      tapSuccess();
      if (payload.attachment_file_id) {
        setText("");
        setReplyTo(null);
      }
      load();
    } catch (e) {
      tapError();
      // Remove the optimistic bubble on failure
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
      toast.error(e?.response?.data?.detail || "Errore invio");
    } finally { setSending(false); }
  };

  const sendText = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    tapLight();
    await doSend({ text: trimmed });
  };

  const onFile = async (e) => {
    const rawFile = e.target.files?.[0];
    e.target.value = "";
    if (!rawFile) return;
    if (!/^image\//.test(rawFile.type)) { toast.error("Solo immagini"); return; }
    try {
      const file = await compressImage(rawFile, { maxSide: 1800, quality: 0.82 });
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await doSend({ text: "", attachment_file_id: data.id, attachment_type: "image" });
    } catch {
      toast.error("Upload foto fallito");
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return messages;
    const q = search.toLowerCase();
    return messages.filter((m) => (m.text || "").toLowerCase().includes(q));
  }, [messages, search]);

  const isMine = (m) => (isBroadcast ? m.sender_user === user?.name : m.sender_store === user?.storeId);

  return (
    <div className="mobile-frame chat-detail-frame" data-testid="chat-detail-page">
      {selectMode ? (
        <div className="tf-chat-select-header" data-testid="chat-select-header">
          <button
            type="button"
            data-testid="chat-select-cancel"
            className="tf-icon-btn ghost"
            onClick={exitSelectMode}
            aria-label="Annulla"
          >
            <IoClose size={22} />
          </button>
          <div className="tf-chat-select-count" data-testid="chat-select-count">
            {selectedIds.size} selezionat{selectedIds.size === 1 ? "o" : "i"}
          </div>
          <button
            type="button"
            data-testid="chat-select-all"
            className="tf-icon-btn ghost"
            onClick={selectAll}
            aria-label="Seleziona tutti"
            title="Seleziona tutti"
          >
            <IoCheckboxOutline size={20} />
          </button>
          <button
            type="button"
            data-testid="chat-select-delete"
            className="tf-icon-btn red"
            onClick={deleteSelected}
            disabled={selectedIds.size === 0}
            aria-label="Elimina selezionati"
          >
            <IoTrashBinOutline size={20} />
          </button>
        </div>
      ) : (
        <AppHeader
          title={title}
          showBack
          backTo="/chat"
          variant="fixed"
          right={
            <button
              type="button"
              className="tf-icon-btn"
              onClick={() => setSearchOpen((v) => !v)}
              data-testid="chat-search-toggle"
              aria-label="Cerca"
            >
              <IoSearchOutline size={20} />
            </button>
          }
        />
      )}
      {searchOpen && (
        <div style={{ padding: "8px 16px", background: "var(--tf-surface)", borderBottom: "1px solid var(--tf-border)" }}>
          <div className="tf-input-wrap">
            <input
              autoFocus
              className="tf-input"
              placeholder="Cerca nei messaggi…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="chat-search-input"
              style={{ paddingRight: 40 }}
            />
            {search && (
              <button
                type="button"
                className="tf-icon-btn"
                style={{ position: "absolute", right: 5, top: "50%", transform: "translateY(-50%)", width: 32, height: 32 }}
                onClick={() => setSearch("")}
                aria-label="Cancella"
              >
                <IoClose size={16} />
              </button>
            )}
          </div>
          <div style={{ fontSize: 12, color: "var(--tf-muted)", marginTop: 4 }}>
            {search ? `${filtered.length} risultati` : ""}
          </div>
        </div>
      )}

      <div className="tf-chat-body" data-testid="chat-messages">
        {loading && <div className="tf-empty">{t("loading")}</div>}
        {!loading && filtered.length === 0 && (
          <div className="tf-empty"><p>{search ? "Nessun risultato" : "Nessun messaggio ancora"}</p></div>
        )}
        {filtered.map((m) => (
          <MessageBubble
            key={m.id}
            m={m}
            mine={isMine(m)}
            onReply={() => setReplyTo(m)}
            storeImage={storeImages[m.sender_store]}
            highlight={search.trim() ? search : ""}
            onOpenActions={(el) => selectMode ? toggleSelected(m.id) : setActionsheetMsg({ msg: m, anchorEl: el })}
            onReact={(emoji) => reactToMessage(m, emoji)}
            myReactor={myReactor}
            selectMode={selectMode}
            selected={selectedIds.has(m.id)}
            onTapWhenSelecting={() => toggleSelected(m.id)}
          />
        ))}
        {typing.length > 0 && (
          <div className="tf-chat-typing" data-testid="chat-typing">
            <StoreAvatar storeId={typing[0].sender_store || "jannelli"} size={22} imageUrl={storeImages[typing[0].sender_store]} />
            <span className="tf-typing-dots"><span></span><span></span><span></span></span>
            <span style={{ marginLeft: 6, fontSize: 12, color: "var(--tf-muted)" }}>
              {typing.map((x) => x.user_name).join(", ")} sta scrivendo…
            </span>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {canPost && (
        <>
          {replyTo && (
            <div className="tf-reply-preview" data-testid="chat-reply-preview">
              <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, color: "var(--tf-red)", fontWeight: 600 }}>
                  Risposta a {replyTo.sender_user}
                </div>
                <div style={{ fontSize: 13, color: "var(--tf-muted)", textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden" }}>
                  {replyTo.text || (replyTo.attachment_type === "image" ? "📷 Foto" : replyTo.attachment_type === "audio" ? "🎤 Audio" : "")}
                </div>
              </div>
              <button type="button" className="tf-icon-btn" onClick={() => setReplyTo(null)} data-testid="chat-reply-cancel" aria-label="Annulla risposta">
                <IoClose size={16} />
              </button>
            </div>
          )}
          <div className="tf-chat-composer" data-testid="chat-composer">
            <button
              type="button"
              className="tf-icon-btn"
              onClick={() => setAttachMenuOpen((v) => !v)}
              data-testid="chat-attach-toggle"
              aria-label="Allega"
            >
              <IoAttachOutline size={20} />
            </button>
            <input
              data-testid="chat-input"
              className="tf-input"
              placeholder="Scrivi un messaggio…"
              value={text}
              onChange={(e) => { setText(e.target.value); sendTypingBeat(); }}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendText(); } }}
            />
            <button
              data-testid="chat-send"
              className="tf-icon-btn red"
              onClick={sendText}
              disabled={sending || !text.trim()}
              aria-label="Invia"
              title="Invia"
            >
              <IoSendOutline size={20} />
            </button>
            <button
              data-testid="chat-mic"
              className="tf-icon-btn yellow"
              onClick={() => setAudioOpen(true)}
              aria-label="Registra audio"
              title="Registra audio"
            >
              <IoMicOutline size={20} />
            </button>
          </div>
          {attachMenuOpen && (
            <div className="tf-attach-menu" data-testid="chat-attach-menu">
              <button type="button" onClick={() => { setAttachMenuOpen(false); fileImgRef.current?.click(); }} data-testid="chat-attach-image">
                <IoImageOutline size={22} /> Foto
              </button>
              <button type="button" onClick={() => setAttachMenuOpen(false)} data-testid="chat-attach-close">Annulla</button>
            </div>
          )}
        </>
      )}

      <input ref={fileImgRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: "none" }} onChange={onFile} data-testid="chat-attach-image-input" />
      <AudioRecorder
        open={audioOpen}
        onCancel={() => setAudioOpen(false)}
        onReady={async ({ file_id, duration }) => {
          setAudioOpen(false);
          await doSend({ text: "", attachment_file_id: file_id, attachment_type: "audio", attachment_duration: duration });
        }}
      />

      {actionsheetMsg && !forwardOpen && (
        <ChatReactionsPopover
          msg={actionsheetMsg.msg}
          anchorEl={actionsheetMsg.anchorEl}
          onClose={() => setActionsheetMsg(null)}
          onReact={(emoji) => { reactToMessage(actionsheetMsg.msg, emoji); setActionsheetMsg(null); }}
          onCopy={() => copyMessage(actionsheetMsg.msg)}
          onReply={() => { setReplyTo(actionsheetMsg.msg); setActionsheetMsg(null); }}
          onForward={() => setForwardOpen(true)}
          onDelete={() => deleteMessage(actionsheetMsg.msg)}
          onSelect={() => enterSelectMode(actionsheetMsg.msg.id)}
          canDelete={isTitolare || actionsheetMsg.msg.sender_user === user?.name}
          myReactor={myReactor}
        />
      )}
      {actionsheetMsg && forwardOpen && (
        <ForwardSheet
          user={user}
          isTitolare={isTitolare}
          onCancel={() => setForwardOpen(false)}
          onPick={forwardTo}
        />
      )}
    </div>
  );
}

/** Anchored reactions popover: emojis chip sits ABOVE the message, action bar BELOW.
 *  Backdrop is transparent so the chat stays visible; tap anywhere closes it. */
function ChatReactionsPopover({ msg, anchorEl, onClose, onReact, onCopy, onReply, onForward, onDelete, onSelect, canDelete, myReactor }) {
  const emojis = ["👍", "❤️", "😂", "😮", "✅", "🙏"];
  const [pos, setPos] = useState({ top: 0, left: 0, bottomTop: 0, width: 260, above: true });

  useEffect(() => {
    if (!anchorEl) return;
    const update = () => {
      const rect = anchorEl.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const chipW = 288;
      const barW = 280;
      // Center chip horizontally over the bubble but clamp to viewport (12px margin)
      const centerX = rect.left + rect.width / 2;
      const chipLeft = Math.max(12, Math.min(vw - chipW - 12, centerX - chipW / 2));
      const barLeft = Math.max(12, Math.min(vw - barW - 12, centerX - barW / 2));
      // Chip goes ABOVE the message unless there's not enough room
      const spaceAbove = rect.top;
      const spaceBelow = vh - rect.bottom;
      const chipHeight = 56;
      const barHeight = 52;
      let chipTop, barTop;
      if (spaceAbove >= chipHeight + 12) {
        chipTop = rect.top - chipHeight - 8;
      } else {
        chipTop = rect.bottom + 8;
      }
      if (spaceBelow >= barHeight + 12) {
        barTop = rect.bottom + 8;
      } else {
        barTop = rect.top - barHeight - 8;
      }
      // If chip and bar would overlap the same row, offset the bar
      if (Math.abs(chipTop - barTop) < chipHeight + 4) {
        barTop = chipTop + chipHeight + 8;
      }
      setPos({ top: chipTop, left: chipLeft, bottomTop: barTop, barLeft, width: chipW });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [anchorEl]);

  const stop = (e) => e.stopPropagation();

  return (
    <div
      className="tf-reactions-anchored-backdrop"
      data-testid="chat-reactions-popover"
      onClick={onClose}
      onContextMenu={(e) => { e.preventDefault(); onClose(); }}
    >
      <div
        className="tf-reactions-chip"
        data-testid="chat-reactions-strip"
        style={{ top: pos.top, left: pos.left, width: pos.width }}
        onClick={stop}
      >
        {emojis.map((e) => {
          const isMine = (msg.reactions?.[e] || []).includes(myReactor);
          return (
            <button
              key={e}
              data-testid={`chat-react-${e}`}
              className={`tf-reactions-emoji-btn ${isMine ? "mine" : ""}`}
              onClick={() => onReact(e)}
              aria-label={`Reagisci con ${e}`}
            >
              {e}
            </button>
          );
        })}
      </div>
      <div
        className="tf-reactions-actionbar"
        style={{ top: pos.bottomTop, left: pos.barLeft }}
        onClick={stop}
      >
        <button
          type="button"
          className="tf-reactions-action-btn"
          data-testid="chat-action-reply"
          onClick={onReply}
        >
          <IoArrowUndoOutline size={18} />
          <span>Rispondi</span>
        </button>
        <button
          type="button"
          className="tf-reactions-action-btn"
          data-testid="chat-action-copy"
          onClick={onCopy}
        >
          <IoCopyOutline size={18} />
          <span>Copia</span>
        </button>
        <button
          type="button"
          className="tf-reactions-action-btn"
          data-testid="chat-action-forward"
          onClick={onForward}
        >
          <IoShareOutline size={18} />
          <span>Inoltra</span>
        </button>
        {canDelete && (
          <button
            type="button"
            className="tf-reactions-action-btn destructive"
            data-testid="chat-action-delete"
            onClick={onDelete}
          >
            <IoTrashOutline size={18} />
            <span>Elimina</span>
          </button>
        )}
        <button
          type="button"
          className="tf-reactions-action-btn"
          data-testid="chat-action-select"
          onClick={onSelect}
        >
          <IoCheckboxOutline size={18} />
          <span>Seleziona</span>
        </button>
      </div>
    </div>
  );
}

/** Destination picker for forward. */
function ForwardSheet({ user, isTitolare, onCancel, onPick }) {
  const { t } = useI18n();
  const myStore = user?.storeId || "";
  const otherStores = ["jannelli", "mazzini", "materdomini", "parafarmacia"].filter((s) => s !== myStore);
  return (
    <div className="tf-msg-actionsheet-overlay" data-testid="chat-forward-sheet" onClick={onCancel}>
      <div className="tf-msg-actionsheet" onClick={(e) => e.stopPropagation()}>
        <div className="tf-drawer-handle" />
        <div className="tf-h2" style={{ marginBottom: 6 }}>Inoltra a…</div>
        <button
          type="button"
          className="tf-msg-action-item"
          data-testid="chat-forward-broadcast"
          onClick={() => onPick("broadcast")}
        >
          <IoMegaphoneOutline size={20} /> Tutti i negozi (broadcast)
        </button>
        {!isTitolare && otherStores.map((s) => (
          <button
            key={s}
            type="button"
            className="tf-msg-action-item"
            data-testid={`chat-forward-${s}`}
            onClick={() => onPick(s)}
          >
            <IoStorefrontOutline size={20} /> {t(STORE_LABELS[s])}
          </button>
        ))}
        <button
          type="button"
          className="tf-msg-action-item destructive"
          data-testid="chat-forward-cancel"
          onClick={onCancel}
        >
          <IoTrashOutline size={20} /> Annulla
        </button>
      </div>
    </div>
  );
}

/** Single bubble with swipe-to-reply, ticks, attachments and reply-preview. */
function MessageBubble({ m, mine, onReply, storeImage, highlight: hl, onOpenActions, onReact, myReactor, selectMode, selected, onTapWhenSelecting }) {
  const [dx, setDx] = useState(0);
  const startXRef = useRef(null);
  const startYRef = useRef(null);
  const activeRef = useRef(false);
  const longPressTimer = useRef(null);
  const suppressClickRef = useRef(false);
  const bubbleRef = useRef(null);

  const clearLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const onDown = (e) => {
    const p = e.touches ? e.touches[0] : e;
    startXRef.current = p.clientX;
    startYRef.current = p.clientY;
    activeRef.current = true;
    clearLongPress();
    longPressTimer.current = setTimeout(() => {
      suppressClickRef.current = true;
      activeRef.current = false;
      setDx(0);
      onOpenActions?.(bubbleRef.current);
    }, 500);
  };
  const onMove = (e) => {
    if (!activeRef.current || startXRef.current === null) return;
    const p = e.touches ? e.touches[0] : e;
    const rawDx = p.clientX - startXRef.current;
    const rawDy = Math.abs(p.clientY - startYRef.current);
    if (Math.abs(rawDx) > 8 || rawDy > 8) clearLongPress();
    if (rawDy > 24) { activeRef.current = false; setDx(0); return; }
    if (rawDx > 0) setDx(Math.min(rawDx, 100));
  };
  const onUp = () => {
    clearLongPress();
    if (!activeRef.current) return;
    activeRef.current = false;
    if (dx >= SWIPE_THRESHOLD) onReply?.();
    setDx(0);
    // Prevent the incidental click after touch
    setTimeout(() => { suppressClickRef.current = false; }, 60);
  };

  const isReservation = m.kind === "reservation";
  const isImg = m.attachment_type === "image" && m.attachment_url;
  const isAudio = m.attachment_type === "audio" && m.attachment_url;
  const url = resolveAttachmentUrl(m.attachment_url);
  const readByOther = !!m.read_by_other;
  const reactions = m.reactions || {};
  const reactionEntries = Object.entries(reactions).filter(([, arr]) => (arr || []).length > 0);

  return (
    <div
      className={`tf-chat-bubble-wrap ${mine ? "mine" : ""} ${m.__pending ? "pending" : ""} ${selectMode ? "selecting" : ""} ${selected ? "selected" : ""}`}
      onTouchStart={selectMode ? undefined : onDown} onTouchMove={selectMode ? undefined : onMove} onTouchEnd={selectMode ? undefined : onUp}
      onMouseDown={selectMode ? undefined : onDown} onMouseMove={selectMode ? undefined : onMove} onMouseUp={selectMode ? undefined : onUp} onMouseLeave={selectMode ? undefined : () => { clearLongPress(); onUp(); }}
      onClick={selectMode ? (e) => { e.preventDefault(); e.stopPropagation(); onTapWhenSelecting?.(); } : undefined}
      onContextMenu={(e) => { e.preventDefault(); if (!selectMode) onOpenActions?.(bubbleRef.current); }}
      style={{ transform: `translateX(${selectMode ? 0 : (mine ? -dx : dx)}px)`, transition: dx === 0 ? "transform 180ms ease" : "none" }}
      data-testid={`chat-msg-${m.id}`}
    >
      {dx > 20 && (
        <span className="tf-swipe-hint" style={{ opacity: Math.min(dx / SWIPE_THRESHOLD, 1) }}>
          <IoArrowUndoOutline size={18} />
        </span>
      )}
      <div ref={bubbleRef} className={`tf-chat-bubble ${mine ? "mine" : ""} ${isReservation ? "reservation" : ""}`}>
        {!mine && (
          <div className="tf-chat-bubble-sender" style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <StoreAvatar storeId={m.sender_store} size={18} imageUrl={storeImage} />
            <span>{m.sender_user}</span>
          </div>
        )}
        {m.meta?.forwarded_from && (
          <div className="tf-chat-quoted" style={{ fontStyle: "italic" }}>
            <div className="tf-chat-quoted-sender">↪ Inoltrato</div>
          </div>
        )}
        {m.reply_to && (
          <div className="tf-chat-quoted" data-testid={`chat-quoted-${m.id}`}>
            <div className="tf-chat-quoted-sender">{m.reply_to.sender_user}</div>
            <div className="tf-chat-quoted-text">{m.reply_to.text}</div>
          </div>
        )}
        {isImg && (
          <img
            src={url}
            alt="allegato"
            className="tf-chat-image"
            loading="lazy"
            data-testid={`chat-image-${m.id}`}
            onClick={() => !suppressClickRef.current && window.open(url, "_blank")}
          />
        )}
        {isAudio && (
          <audio controls src={url} className="tf-chat-audio" data-testid={`chat-audio-${m.id}`} />
        )}
        {m.text && (
          <div className="tf-chat-bubble-text">
            {hl ? highlight(m.text, hl) : m.text}
          </div>
        )}
        <div className="tf-chat-bubble-meta">
          <span className="tf-chat-bubble-time">{formatDateTimeIT(m.created_at)}</span>
          {mine && (
            <span className={`tf-chat-tick ${readByOther ? "read" : ""}`} data-testid={`tick-${m.id}`} aria-label={readByOther ? "letto" : "inviato"}>
              {readByOther ? <IoCheckmarkDone size={16} /> : <IoCheckmark size={16} />}
            </span>
          )}
        </div>
        {reactionEntries.length > 0 && (
          <div className="tf-msg-reactions" data-testid={`chat-reactions-${m.id}`}>
            {reactionEntries.map(([emoji, arr]) => (
              <span
                key={emoji}
                className={`tf-msg-reaction ${arr.includes(myReactor) ? "mine" : ""}`}
                onClick={(e) => { e.stopPropagation(); onReact?.(emoji); }}
                data-testid={`chat-reaction-${m.id}-${emoji}`}
              >
                {emoji} {arr.length}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
